import { useCallback, useId, useState } from 'react';
import { Box, FlexBox, Icon, Typography, getTokenCssVar } from '@vp/swan';
import type { ReactNode } from 'react';
import { __ } from '@99designs/i18n';
import { assertNever } from '../../messages';
import { ChatAssistantAvatar } from '../ChatAssistantAvatar';
import { GenerativeUiMessage } from '../generative-ui';
import { CtaLinkAnchor } from './CtaLinkAnchor';
import { TypewriterText } from './TypewriterText';
import { renderInlineMarkdown, stripInlineMarkdown } from './inlineMarkdown';
import type { ChatImageAttachment, DisplayMessage } from '../../messages';

export interface MessageBubbleProps {
    message: DisplayMessage;
    /** Whether to reveal this message word by word — true for at most one message at a time. */
    animate?: boolean;
    /** Host hook for reporting a call-to-action link click; the library emits no analytics itself. */
    onCtaLinkClick?: (url: string, label: string) => void;
}

export function MessageBubble({ message, animate = false, onCtaLinkClick }: MessageBubbleProps) {
    const bubbleContentId = useId();
    const isUser = message.role === 'user';
    const [revealComplete, setRevealComplete] = useState(!animate);
    const handleRevealComplete = useCallback(() => setRevealComplete(true), []);
    let ariaMessage = '';
    let ariaLabel: string | undefined;
    let ariaLabelledBy: string | undefined;
    let bubbleContent: ReactNode | null;
    let hideBubbleContentFromAccessibilityTree = true;

    switch (message.kind) {
        case 'text': {
            const revealing = animate && !revealComplete;
            const ctaLink = message.ctaLink;
            const showCta = ctaLink !== undefined && !revealing;
            ariaMessage = showCta
                ? `${stripInlineMarkdown(message.content)} ${ctaLink.label}`
                : stripInlineMarkdown(message.content);
            // A focusable link must never sit inside an aria-hidden subtree, so a bubble showing
            // its call to action exposes its content instead of relying on the summary label.
            hideBubbleContentFromAccessibilityTree = !showCta;
            const cta = showCta ? <CtaLinkAnchor link={ctaLink} onClick={onCtaLinkClick} /> : null;
            bubbleContent = revealing ? (
                <TypewriterText text={message.content} onRevealComplete={handleRevealComplete} />
            ) : (
                <Typography style={{ wordBreak: 'break-word' }}>
                    {renderInlineMarkdown(message.content)}
                    {cta}
                </Typography>
            );
            break;
        }
        case 'helpRequest':
            ariaMessage = __('Help me answer');
            bubbleContent = <Typography>{ariaMessage}</Typography>;
            break;
        case 'generativeUi':
            // Generative UI replies already render the customer's chosen answer, so this branch
            // exposes the live subtree instead of duplicating that content in an aria-label.
            ariaLabelledBy = bubbleContentId;
            hideBubbleContentFromAccessibilityTree = false;
            bubbleContent = <GenerativeUiMessage message={message} />;
            break;
        case 'imageUpload':
            ariaMessage = imageUploadAriaText(message.attachments, message.caption);
            bubbleContent = (
                <FlexBox flexDirection="column" gap={3}>
                    <FlexBox
                        gap={3}
                        style={{
                            flexWrap: 'wrap',
                            marginBottom: message.caption ? getTokenCssVar('SwanSemSpace3') : '0',
                        }}
                    >
                        {message.attachments.map((att) =>
                            att.mediaType.startsWith('image/') ? (
                                <img
                                    key={att.uploadId}
                                    src={att.previewUrl}
                                    alt={att.fileName}
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                                        display: 'block',
                                    }}
                                />
                            ) : (
                                <Box
                                    key={att.uploadId}
                                    style={{
                                        width: '200px',
                                        borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: getTokenCssVar('SwanSemSpace2'),
                                        padding: getTokenCssVar('SwanSemSpace4'),
                                        background: 'var(--swan-sem-color-background-standard)',
                                    }}
                                >
                                    <Icon iconType="fileGeneric" size="40p" alt={att.fileName} />
                                    <Typography
                                        fontSkin="body-small"
                                        style={{
                                            wordBreak: 'break-all',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {att.fileName}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </FlexBox>
                    {message.caption && <Typography>{message.caption}</Typography>}
                </FlexBox>
            );
            break;
        case 'unparseable':
            ariaMessage = __("Sorry, we couldn't display the response.");
            bubbleContent = <Typography>{ariaMessage}</Typography>;
            break;
        case 'unrecognizedJson':
            // This is the known-but-out-of-scope-for-this-slice bucket (for example image-upload rows);
            // showing raw JSON would be worse than showing nothing, and a future image slice will replace it.
            bubbleContent = null;
            break;
        default:
            assertNever(message);
    }

    if (bubbleContent === null) {
        return null;
    }

    if (!ariaLabelledBy) {
        ariaLabel = isUser
            ? __('You: {{message}}', {
                  message: ariaMessage,
                  _context: 'Accessible label for a customer message in the AI brief chat',
              })
            : __('Agent: {{message}}', {
                  message: ariaMessage,
                  _context: 'Accessible label for an AI assistant message in the brief chat',
              });
    }

    return (
        <FlexBox
            role="article"
            data-chat-message
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            justifyContent={isUser ? 'flex-end' : 'flex-start'}
            gap={isUser ? undefined : 2}
            mb={5}
        >
            {!isUser && <ChatAssistantAvatar />}
            <Box
                id={ariaLabelledBy}
                aria-hidden={hideBubbleContentFromAccessibilityTree ? 'true' : undefined}
                backgroundColor={isUser ? 'accent' : 'standard'}
                paddingY={4}
                paddingX={5}
                style={{
                    maxWidth: '576px',
                    minWidth: 0,
                    // SWAN exposes no borderRadius core prop, so this stays inline —
                    // the value is still a token.
                    borderRadius: getTokenCssVar('SwanSemBorderRadiusStrong'),
                }}
            >
                {bubbleContent}
            </Box>
        </FlexBox>
    );
}

function imageUploadAriaText(attachments: ChatImageAttachment[], caption?: string): string {
    const count = attachments.length;
    const filesLabel =
        count === 1
            ? __('Attached 1 file')
            : __('Attached {{count}} files', { count: String(count) });
    return caption ? `${filesLabel}. ${caption}` : filesLabel;
}
